import { useEffect, useId, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { HaiSelect } from '@/components/HaiSelect';
import { LightboxShell } from '@/components/LightboxShell';
import { BRAND_KITS_UI_VISIBLE } from '@/features/brandKits/brandKitsGate';
import {
  CoverControls,
  CoverPreview,
  useCoverPicker,
  type CoverDraft,
} from '@/features/covers/CoverPicker';
import { mapCoverErrorMessage } from '@/features/covers/coverErrorMap';
import { prepareCoverUpload } from '@/lib/coverImageFile';
import { PROJECT_BRAND_KIT_PATCH_SUPPORTED } from '@/services/projectsService';
import { services } from '@/services';
import type { BrandKit, Project } from '@/types';

/**
 * ProjectEditDialog — 編輯專案資訊(R292 / FB-20260828-0008;對齊 D3 `projects.js`
 * 表單的「編輯專案資訊」模式)。
 * -----------------------------------------------------------------------------
 * 可編輯:名稱(必填 ≤100)/ 說明(≤120,對齊建立表單)/ 封面(色塊 PATCH;圖片走
 * `POST /projects/{pid}/cover`,與建立流程同一條)/ **品牌規範引用**(R292.5:
 * owner 拍板 Q-15 後 BE 開欄 —— `''` 清除、非空換引用;錯誤 404 `BRAND_KIT_NOT_FOUND`)。
 *
 * 品牌下拉由 {@link PROJECT_BRAND_KIT_PATCH_SUPPORTED} 控制 —— **R397 起開**(BE main
 * 2026-08-29 已開欄,取證見 gate 常數註解)。gate 關著或 kits 清單載不到時,品牌列退回
 * 唯讀顯示現值 —— 不畫一顆選了也送不對的下拉。
 *
 * **R397(owner 2026-10-07「編輯的時候也能直接用圖一(建立)的樣貌」)— 版型對齊
 * `ProjectCreateDialog`**:頂部封面預覽(整塊可點上傳,104px)→ 名稱 → 說明(placeholder
 * + n/120 計數)→ 品牌規範(HaiSelect + hint)→ 封面色票列(與頂部預覽共用**同一個**
 * `useCoverPicker` controller —— R293 紅線:拆兩塊不拆 state)。既有**圖片封面**在沒動
 * 封面前,預覽直接顯 server 給的那張(draft 是 none 時以 `project.cover` 墊底),不再
 * 顯成漸層 fallback。
 *
 * D3 有「工作區搬移」欄;BE 無對應端點(`group_id` IMMUTABLE)→ 不做。
 *
 * R393(CIS)— 外框改共用 {@link LightboxShell} `narrow`(= D3 `.pj-modal` 560px,與建立表單同一個):
 * 原本自刻遮罩只有 ESC + 遮罩、**沒有關閉鈕**;換殼後三個出口齊、ESC 走分層。送出中(`busy`)
 * 三個出口一起暫時鎖住(`closeIfIdle`)—— 存到一半關窗,使用者會以為沒存。
 * 欄位標題對齊建立表單的「想…」口吻 + 必填 / 選填 pill(D3 `.pj-lbl em`;同一張表單
 * create / edit 共用版型)。品牌下拉改 `HaiSelect`(CIS 禁原生 `<select>`)。
 */
export function ProjectEditDialog({
  project,
  onClose,
  onSaved,
}: {
  project: Project;
  onClose: () => void;
  /** 存檔成功 → 回最新 Project(呼叫端更新列表);封面圖另傳成功也會再回一次。 */
  onSaved: (updated: Project) => void;
}) {
  const { t } = useTranslation('projects');
  // 封面錯誤字典掛在 common(與建立表單共用同一組錯誤碼)
  const { t: tCommon } = useTranslation('common');
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? '');
  const [cover, setCover] = useState<CoverDraft>(() =>
    project.cover.kind === 'color' ? { kind: 'color', color: project.cover.color } : { kind: 'none' },
  );
  const [coverTouched, setCoverTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // R292.5(Q-15)— 品牌引用可改:'' = 不引用(送 wire 空字串清除)。
  const [brandKitId, setBrandKitId] = useState(project.brandKitId ?? '');
  // kits 清單(null = 載入中/失敗 → 退回唯讀顯示,不畫空下拉)。
  const [kits, setKits] = useState<BrandKit[] | null>(null);
  useEffect(() => {
    // R397 — 品牌規範 UI 隱藏(brandKitsGate)或 wire gate 關 → 不抓 kits。
    if (!BRAND_KITS_UI_VISIBLE || !PROJECT_BRAND_KIT_PATCH_SUPPORTED) return;
    let cancelled = false;
    services.brandKitsService
      .list()
      .then((list) => {
        if (!cancelled) setKits(list);
      })
      .catch(() => {
        /* 退回唯讀顯示,不擋其他欄位編輯 */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ESC / 遮罩 / 關閉鈕都由 LightboxShell 提供(ESC 走 `escapeLayer` 分層);送出中一律不關。
  const closeIfIdle = (): void => {
    if (!busy) onClose();
  };
  const brandLabelId = useId();

  // R397 — 預覽(頂)與色票列(底)共用同一個 controller(R293:拆兩塊不拆 state)。
  const picker = useCoverPicker({
    value: cover,
    onChange: (v) => {
      setCover(v);
      setCoverTouched(true);
    },
    disabled: busy,
  });
  // 既有圖片封面:draft 還是 none(沒動過)時,預覽顯 server 的那張 —— CoverDraft 只帶
  // 本地 File,server 圖片進不了 draft,所以在顯示層墊底;一旦使用者選了色塊 / 新圖,
  // draft 接手。只動 previewCover(CoverControls 不讀它)。
  const editPicker =
    cover.kind === 'none' && !coverTouched && project.cover.kind === 'image'
      ? { ...picker, previewCover: project.cover }
      : picker;

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      let updated = await services.projectsService.update(project.projectId, {
        name: trimmed,
        description: description.trim() || null,
        // 封面:只在使用者動過時送(色塊 / 清空走 PATCH;圖片另走 multipart)
        ...(coverTouched && cover.kind !== 'image'
          ? { cover: cover.kind === 'color' ? { kind: 'color' as const, color: cover.color } : { kind: 'none' as const } }
          : {}),
        // 品牌引用:gate 開著**且**有變才送(undefined = 不改;'' = 清除 —— 見
        // ProjectUpdateRequest 註)。gate 關著時一個字都不能夾帶:BE main `extra=forbid`。
        ...(PROJECT_BRAND_KIT_PATCH_SUPPORTED && brandKitId !== (project.brandKitId ?? '')
          ? { brandKitId }
          : {}),
      });
      if (coverTouched && cover.kind === 'image') {
        try {
          const prepared = await prepareCoverUpload(cover.file);
          updated = await services.projectsService.setCoverImage(project.projectId, prepared);
        } catch (coverErr) {
          // 封面失敗不回滾其餘欄位(名稱/說明已存好)—— 顯示封面錯誤讓使用者重試即可
          setError(mapCoverErrorMessage(coverErr, tCommon));
          onSaved(updated);
          setBusy(false);
          return;
        }
      }
      onSaved(updated);
      onClose();
    } catch (e) {
      setError(mapCoverErrorMessage(e, tCommon));
      setBusy(false);
    }
  };

  const required = t('edit.required');
  const optional = t('edit.optional');

  return (
    <LightboxShell
      size="narrow"
      ariaLabel={t('edit.title')}
      closeTitle={tCommon('actions.close')}
      onClose={closeIfIdle}
      data-testid="project-edit-dialog"
      bodyStyle={bodyStyle}
      head={<h2 style={titleStyle}>{t('edit.title')}</h2>}
      footer={
        <div style={footRowStyle}>
          {/* CIS:取消 = 幽靈灰 `.btn-ghost`、確認 = 實心 teal `.btn-solid`;停用態走 class
              (`--btn-disabled-*`,不再整顆 opacity 調淡) */}
          <button
            type="button"
            className="hai-btn-ghost"
            onClick={closeIfIdle}
            disabled={busy}
            data-testid="project-edit-cancel"
          >
            {t('edit.cancel')}
          </button>
          <button
            type="button"
            className="hai-btn-solid"
            onClick={() => void submit()}
            disabled={busy || !name.trim()}
            data-testid="project-edit-save"
          >
            {busy ? t('edit.saving') : t('edit.save')}
          </button>
        </div>
      }
    >
      {/* R397 — 封面預覽放最上(與建立表單同版型;整塊可點 = 上傳)。 */}
      <CoverPreview picker={editPicker} previewLabel={name || project.name} height={104} />

      <FieldLabel htmlFor="pe-name" flag={required} flagTone="required">
        {t('edit.nameLabel')}
      </FieldLabel>
      <input
        id="pe-name"
        value={name}
        maxLength={100}
        onChange={(e) => setName(e.target.value)}
        style={inputStyle}
        data-testid="project-edit-name"
      />
      <FieldLabel htmlFor="pe-desc" flag={optional} flagTone="optional">
        {t('edit.descLabel')}
      </FieldLabel>
      <textarea
        id="pe-desc"
        rows={3}
        maxLength={120}
        value={description}
        onChange={(e) => setDescription(e.target.value.slice(0, 120))}
        placeholder={t('edit.descPh')}
        style={{ ...inputStyle, resize: 'vertical' }}
        data-testid="project-edit-desc"
      />
      {/* R397 — n/120 計數(與建立表單同;owner:編輯直接用建立的樣貌)。 */}
      <span style={fieldHintStyle}>{t('edit.descCount', { used: description.length, max: 120 })}</span>
      {/* R397 — 品牌規範 UI 隱藏(brandKitsGate;owner 授權才開):整欄不渲染。 */}
      {BRAND_KITS_UI_VISIBLE && (
      <>
      <FieldLabel id={brandLabelId} flag={optional} flagTone="optional">
        {t('edit.brandLabel')}
      </FieldLabel>
      {PROJECT_BRAND_KIT_PATCH_SUPPORTED && kits !== null ? (
        <>
          {/* R393 — 原生 `<select>` → HaiSelect(field)。R345 語意不變:''(未引用)是真的選項;
              現值不在清單(kit 被刪 / 跨群殘留)→ 補一個 id 選項,不讓下拉顯空白或誤顯第一項。 */}
          <HaiSelect
            icon="shield"
            value={brandKitId}
            onChange={(v) => setBrandKitId(v)}
            disabled={busy}
            aria-labelledby={brandLabelId}
            data-testid="project-edit-brand-select"
            options={[
              { value: '', label: t('edit.brandNone') },
              ...kits.map((k) => ({ value: k.brandKitId, label: k.name })),
              ...(project.brandKitId && !kits.some((k) => k.brandKitId === project.brandKitId)
                ? [{ value: project.brandKitId, label: project.brandKitId }]
                : []),
            ]}
          />
          <span style={brandHintStyle}>{t('edit.brandHint')}</span>
        </>
      ) : (
        // kits 載不到 / gate 關 → 唯讀顯示現值(不畫選了也送不對的空下拉)
        <div style={brandRowStyle} data-testid="project-edit-brand">
          {project.brandKitId ?? t('edit.brandNone')}
        </div>
      )}
      </>
      )}
      <FieldLabel flag={optional} flagTone="optional">
        {t('edit.coverLabel')}
      </FieldLabel>
      <CoverControls picker={picker} />
      {error && (
        <div role="alert" style={errStyle} data-testid="project-edit-error">
          {error}
        </div>
      )}
    </LightboxShell>
  );
}

/**
 * 欄位標題 + 必填 / 選填 pill(D3 `.pj-lbl` + `.pj-lbl em`;與 `ProjectCreateDialog` 同版型)。
 * 沒有 `htmlFor` 的(封面是一組按鈕 / 品牌是 HaiSelect 用 aria-labelledby 指回來)用 `<div>`。
 */
function FieldLabel({
  htmlFor,
  id,
  flag,
  flagTone,
  children,
}: {
  htmlFor?: string;
  id?: string;
  flag: string;
  flagTone: 'required' | 'optional';
  children: ReactNode;
}) {
  const pill = <em style={flagTone === 'required' ? requiredPillStyle : optionalPillStyle}>{flag}</em>;
  return htmlFor ? (
    <label htmlFor={htmlFor} id={id} style={lblStyle}>
      {children}
      {pill}
    </label>
  ) : (
    <div id={id} style={lblStyle}>
      {children}
      {pill}
    </div>
  );
}

// ─── styles(對齊 ProjectCreateDialog / D3 `.pj-*` 燈箱;色彩一律 token)────────────────

const titleStyle: CSSProperties = { margin: 0, flex: 1, fontSize: 15, fontWeight: 700, color: 'var(--text-strong)' };
/** 內容區(外框 padding 由 `LightboxShell` 給)。 */
const bodyStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};
/** footer 兩顆鈕靠右(`LightboxShell` 的 foot 是 flex + gap 8)。 */
const footRowStyle: CSSProperties = { marginLeft: 'auto', display: 'flex', gap: 8 };
/** CIS 欄位標籤 12 / 500 / 次文字(區塊標題 13/800 > 標籤 > hint 11)。 */
const lblStyle: CSSProperties = {
  marginTop: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--ink-500)',
};
/** D3 `.pj-lbl em` — teal pill;`--concept-*` 是會隨主題翻的 teal pill 對。 */
const requiredPillStyle: CSSProperties = {
  fontStyle: 'normal',
  fontSize: 10.5,
  fontWeight: 700,
  color: 'var(--concept-fg)',
  background: 'var(--concept-bg)',
  borderRadius: 'var(--r-pill)',
  padding: '1px 7px',
};
const optionalPillStyle: CSSProperties = {
  ...requiredPillStyle,
  color: 'var(--ink-500)',
  background: 'var(--ink-850)',
};
const inputStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  borderRadius: 8,
  // ⚠️ 原本是 `--slate-200` —— theme.css 沒有這顆 token,整條 border 宣告作廢 = 白底白框看不到欄位
  border: '1px solid var(--hairline)',
  background: 'var(--ink-950)',
  color: 'var(--text-primary)',
  fontSize: 13,
  fontFamily: 'inherit',
};
const brandRowStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  padding: '8px 10px',
  borderRadius: 8,
  background: 'color-mix(in oklab, var(--slate-100) 55%, transparent)',
  fontSize: 12.5,
  color: 'var(--text-primary)',
};
/** CIS hint = 11 / 次文字。 */
const brandHintStyle: CSSProperties = { fontSize: 11, color: 'var(--ink-500)' };
/** R397 — 說明計數(與建立表單的 hint 同級)。 */
const fieldHintStyle: CSSProperties = { fontSize: 11, color: 'var(--ink-500)' };
/** 錯誤 = 玫瑰(CIS 不用 `--danger-*` 表示錯誤)。 */
const errStyle: CSSProperties = {
  marginTop: 8,
  fontSize: 12,
  color: 'var(--rose-700)',
};
